import { lazy, Suspense, useMemo } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

// eslint-disable-next-line
import '../../store/src/lib/mocks';

import TestPage from './utils/TestPage';
import { PrivateRoute } from './_components';
import LoadingFallback from '../components/layout/loading-fallback';
import { RestrictedRoute } from './components/raytd/guards/RestrictedRoute';
//import ProfileLoader from './layout2/ProfileLoader';
import { Logout } from './layout/logout';
import { PublicReportViewer } from '@/app/features/public-link/PublicReportViewer';
import { useWindowSize } from '@/hooks/useWindowSize';
// const PublicReportViewer = lazy(() => import('./features/public-link/PublicReportViewer'));

// Lazy load layouts
const AppLayout = lazy(() => import('./layout2/AppLayout'));
const AuthLayout = lazy(() => import('@/../ui/src/lib/login/AuthLayout'));

// Import SimpleMobileLayout directly (not lazy) for immediate rendering
import SimpleMobileLayoutImport from './layout2/SimpleMobileLayout';
import MobileProfilePageImport from './features/profile/MobileProfilePage';
import MobileNotAvailable from '@/app/layout2/mobile/NotAvailable';

// Lazy load route modules
const AuthRoutes = lazy(() => import('./features/auth'));
const AcceptTermsPage = lazy(() => import('./features/terms/AcceptTermsPage').then(m => ({ default: m.default })));
const DashboardPage = lazy(() => import('./features/dashboard/DashboardPage'));
const ProfilePage = lazy(() => import('./features/profile/ProfilePage'));
const OrganisationPage = lazy(() => import('./features/organisation/OrganisationPage'));
const AssetRoutes = lazy(() => import('./features/assets'));
const ElementRoutes = lazy(() => import('./features/elements'));
const TestSuiteRoutes = lazy(() => import('./features/testsuites'));
const BlockRoutes = lazy(() => import('./features/blocks'));
const InspectionRoutes = lazy(() => import('./features/inspections'));
const ReportRoutes = lazy(() => import('./features/reports'));
const AdminRoutes = lazy(() => import('./features/admin'));
const MobileOnlyMessage = lazy(() => import('./layout2/mobile/MobilePage'));
const PublishedReportPrintView = lazy(() => import('./features/reports/published/PublishedReportPrintView'));

// Mobile breakpoint - exclude tablets (768px and above)
const MOBILE_BREAKPOINT = 500;

// const ErrorThrowingComponent: React.FC = () => {
//   throw new Error('This is a simulated error');
// };

// Mobile-only message component


const createDesktopRouter = () => createBrowserRouter([
  // Public auth routes
  {
    path: '/report/view/:uuid/*',
    element: <PublicReportViewer />,
  },
  // Print route - public route with token-based authentication
  {
    path: '/reports/:reportId/published/:revisionId/print',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <PublishedReportPrintView />
      </Suspense>
    ),
  },

  {
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <AuthLayout />
      </Suspense>
    ),
    children: [
      {
        path: '/accept-terms',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <AcceptTermsPage />
          </Suspense>
        )
      },
      {
        path: '/*',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <AuthRoutes />
          </Suspense>
        )
      }
    ],
    errorElement: (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-center">
        <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
        <p className="mb-4 text-gray-700">We encountered an error</p>
      </div>
    )
  },
  // Protected routes
  {
    element: <PrivateRoute />,
    children: [
      {
        path: '/',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <AppLayout />
          </Suspense>
        ),
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <DashboardPage />
              </Suspense>
            )
          },
          {
            path: 'profile/*',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ProfilePage />
              </Suspense>
            )
          },
          {
            path: 'organisation/*',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <OrganisationPage />
              </Suspense>
            )
          },
          // Feature routes
          {
            path: 'assets/*',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <AssetRoutes />
              </Suspense>
            )
          },
          {
            path: 'elements/*',
            element: (
              <RestrictedRoute feature="element_groups_module">
                <Suspense fallback={<LoadingFallback />}>
                  <ElementRoutes />
                </Suspense>
              </RestrictedRoute>
            )
          },
          {
            path: 'test-suites/*',
            element: (
              <RestrictedRoute feature="test_suites_module">
                <Suspense fallback={<LoadingFallback />}>
                  <TestSuiteRoutes />
                </Suspense>
              </RestrictedRoute>
            )
          },
          {
            path: 'report-blocks/*',
            element: (
              <RestrictedRoute feature="report_blocks_module">
                <Suspense fallback={<LoadingFallback />}>
                  <BlockRoutes />
                </Suspense>
              </RestrictedRoute>
            )
          },
          {
            path: 'inspections/*',
            element: (
              <RestrictedRoute feature="inspections_module">
                <Suspense fallback={<LoadingFallback />}>
                  <InspectionRoutes />
                </Suspense>
              </RestrictedRoute>
            )
          },
          {
            path: 'reports/*',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ReportRoutes />
              </Suspense>
            )
          },
          {
            path: 'test',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <TestPage />
              </Suspense>
            )
          }
        ]
      },
      // Admin routes
      {
        path: 'admin/*',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <AdminRoutes />
          </Suspense>
        )
      }
    ]
  },
  {
    path: 'logout',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Logout />
      </Suspense>
    )
  },
  // Catch-all route for 404
  {
    path: '*',
    element: (
      <div className="flex h-screen items-center justify-center">
        <h1 className="text-2xl font-semibold">404 - Page Not Found</h1>
      </div>
    )
  }
])

const createMobileRouter = () => createBrowserRouter([
  // Public report viewer (always accessible)
  {
    path: '/report/view/:uuid/*',
    // element: <PublicReportViewer />,
    element: <MobileNotAvailable />,
  },

  // Logout route
  {
    path: '/logout',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Logout />
      </Suspense>
    )
  },


  {
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <AuthLayout />
      </Suspense>
    ),
    children: [
      {
        path: '/*',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <AuthRoutes />
          </Suspense>
        )
      }
    ],
    errorElement: (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-center">
        <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
        <p className="mb-4 text-gray-700">We encountered an error</p>
      </div>
    )
  },
  // Protected mobile routes - limited functionality (BEFORE catch-all)
  {
    element: <PrivateRoute />,
    errorElement: (
      <div style={{ padding: '20px', color: '#090909' }}>
        <h1>Mobile Route Error</h1>
        <p>Something went wrong</p>
      </div>
    ),
    children: [
      {
        path: '/',
        element: <SimpleMobileLayoutImport />,
        errorElement: (
          <div style={{ padding: '20px', color: 'red' }}>
            <h1>SimpleMobileLayout Error</h1>
          </div>
        ),
        children: [
          {
            index: true,
            element: <MobileOnlyMessage />
          },
          {
            path: 'profile/*',
            element: <MobileProfilePageImport />
          },
          // All other routes show mobile message
          {
            path: '*',
            element: <MobileOnlyMessage />
          }
        ]
      }
    ]
  }
])

export const App = () => {
  const { width } = useWindowSize()

  // Build each router ONCE. `createBrowserRouter()` produces a router with its
  // own internal state, so rebuilding it on every `width` change and handing
  // the new instance to <RouterProvider> makes React unmount + remount the
  // entire routed tree — wiping all in-progress form state (raytd/web#29).
  // We therefore create both routers up-front and only *switch* between them
  // when the viewport actually crosses the mobile breakpoint; sub-breakpoint
  // width changes re-render but return the same router reference (no remount).
  const desktopRouter = useMemo(() => createDesktopRouter(), [])
  const mobileRouter = useMemo(() => createMobileRouter(), [])

  const isMobile = width > 0 && width < MOBILE_BREAKPOINT
  const router = isMobile ? mobileRouter : desktopRouter

  return (
    <RouterProvider router={router} />
  )
}

export default App
